/**
 * MKS-351 — the ONE date input in the app.
 *
 * A native `<input type="date">` renders in the *browser's* locale and offers
 * no way to change that, which is how `/invoices/:id` ended up showing a Due
 * Date of `11/20/2026` directly above a Period of `20. 11. 2026`. This control
 * replaces it with a plain text input whose displayed text and accepted
 * keyboard entry both come from `utils/dateFormat`'s active spec, plus a
 * calendar popover built here (English month names, Monday-first weeks) so no
 * browser-locale string reaches the screen.
 *
 * The contract matches the native control it replaces so no call site has to
 * change its state shape: `value` and `onChange` both speak ISO `YYYY-MM-DD`,
 * and `''` means empty. `min`/`max` are ISO too.
 *
 * ## Release flag
 *
 * While the MKS-351 flag is off, `getActiveDateFormat()` is still the legacy
 * Czech spec and this component renders the native `<input type="date">` it
 * replaces — the off branch is today's behaviour, byte for byte. The flag
 * itself is read once in `<App>` (`useDateFormatSync`), never here, so a
 * component test that mounts a page in isolation gets the native control and
 * keeps passing unchanged.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import {
  LEGACY_CZ_DATE_FORMAT,
  dateInputPlaceholder,
  getActiveDateFormat,
  parseDateInput,
  toDateInputText,
  toDateParts,
  toIsoDate,
  todayIso,
} from '../../utils/dateFormat';

export interface DateFieldProps {
  /** ISO `YYYY-MM-DD`, or `''` for empty. Same as the native control's value. */
  value: string;
  /** Fires with ISO `YYYY-MM-DD`, or `''` when the field is cleared. */
  onChange: (next: string) => void;
  id?: string;
  name?: string;
  /** Earliest selectable day, ISO `YYYY-MM-DD`. */
  min?: string;
  /** Latest selectable day, ISO `YYYY-MM-DD`. */
  max?: string;
  required?: boolean;
  disabled?: boolean;
  /** Applied to the text input, so existing layout classes carry over verbatim. */
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'data-testid'?: string;
  autoFocus?: boolean;
  onBlur?: () => void;
}

// MKS-493 — `rounded-ms-control` defaults to 0.375rem whereas this was
// `rounded-lg` (0.5rem), so the token is given the old value explicitly as a
// fallback: `rounded-[var(--ms-radius-control,0.5rem)]` would silently change
// the default theme. Using the control token WITH `rounded-lg` as the
// default-theme value is not expressible in one utility, so the radius is set
// from the token only once Sparkle is active — see `.ms-input` in sparkle.css.
const DEFAULT_INPUT_CLASS =
  'ms-input w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/** Monday-first weekday index (0 = Monday) of the first day of a month. */
function mondayFirstOffset(year: number, month: number): number {
  const jsDay = new Date(Date.UTC(year, month - 1, 1)).getUTCDay(); // 0 = Sunday
  return (jsDay + 6) % 7;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** ISO string comparison is calendar-correct for `YYYY-MM-DD`. */
function outOfRange(iso: string, min?: string, max?: string): boolean {
  if (min && iso < min) return true;
  if (max && iso > max) return true;
  return false;
}

export function DateField({
  value,
  onChange,
  id,
  name,
  min,
  max,
  required,
  disabled,
  className,
  autoFocus,
  onBlur,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'data-testid': dataTestId,
}: DateFieldProps) {
  const spec = getActiveDateFormat();
  // The legacy spec IS the release flag's off state (see the file header), so
  // it is what selects the native control rather than a second flag read.
  const useNativeControl = spec === LEGACY_CZ_DATE_FORMAT;
  const generatedId = useId();
  const inputId = id ?? `date-${generatedId}`;
  const inputClass = className ?? DEFAULT_INPUT_CLASS;

  // What the text box shows. Kept separate from `value` so a half-typed date
  // ("20.1") survives the keystroke that cannot be parsed yet.
  const [text, setText] = useState(() => toDateInputText(value, spec));
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Re-sync when the owner changes the value (a reset, a loaded record) — but
  // never while the field holds text that already means `value`, or typing
  // "7.5.1978" would be rewritten to "07.05.1978" mid-keystroke.
  useEffect(() => {
    setText((current) => (parseDateInput(current, spec) === (value || null) ? current : toDateInputText(value, spec)));
  }, [value, spec]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // The month the calendar is showing: the selected day, else today.
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const parts = toDateParts(value) ?? toDateParts(todayIso())!;
    return { year: parts.year, month: parts.month };
  });

  useEffect(() => {
    const parts = toDateParts(value);
    if (parts) setVisibleMonth({ year: parts.year, month: parts.month });
  }, [value]);

  const handleTextChange = (next: string) => {
    setText(next);
    const parsed = parseDateInput(next, spec);
    if (parsed !== null) {
      onChange(parsed);
    } else if (next.trim() === '') {
      onChange('');
    }
    // Anything else is a date still being typed: hold the keystrokes, leave
    // the committed value alone until it parses or the field is blurred.
  };

  const handleBlur = () => {
    const parsed = parseDateInput(text, spec);
    // Normalise "7.5.78" to "07.05.1978"; discard text that never parsed so the
    // box can never disagree with the value the form will submit.
    setText(parsed ? toDateInputText(parsed, spec) : toDateInputText(value, spec));
    onBlur?.();
  };

  const selectDay = (iso: string) => {
    onChange(iso);
    setText(toDateInputText(iso, spec));
    setOpen(false);
  };

  const grid = useMemo(() => {
    const { year, month } = visibleMonth;
    const lead = mondayFirstOffset(year, month);
    const total = daysInMonth(year, month);
    const cells: (string | null)[] = Array.from({ length: lead }, () => null);
    for (let day = 1; day <= total; day++) cells.push(toIsoDate({ year, month, day }));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [visibleMonth]);

  // Flag off: the native control, exactly as every call site rendered it before
  // MKS-351. Nothing below this line runs in production until the flag is on.
  if (useNativeControl) {
    return (
      <input
        id={inputId}
        name={name}
        type="date"
        value={value}
        min={min}
        max={max}
        required={required}
        disabled={disabled}
        className={inputClass}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        data-testid={dataTestId}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    );
  }

  const shiftMonth = (delta: number) => {
    setVisibleMonth((current) => {
      const zeroBased = current.month - 1 + delta;
      return {
        year: current.year + Math.floor(zeroBased / 12),
        month: ((zeroBased % 12) + 12) % 12 + 1,
      };
    });
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        id={inputId}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={text}
        placeholder={dateInputPlaceholder(spec)}
        required={required}
        disabled={disabled}
        className={inputClass}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        data-testid={dataTestId}
        onChange={(e) => handleTextChange(e.target.value)}
        onBlur={handleBlur}
      />
      <button
        type="button"
        disabled={disabled}
        aria-label={`Open calendar${ariaLabel ? ` for ${ariaLabel}` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-400 hover:text-gray-600 disabled:opacity-40"
      >
        <CalendarIcon className="h-4 w-4" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose a date"
          className="absolute z-30 mt-1 w-64 rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
        >
          <div className="mb-1 flex items-center justify-between">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => shiftMonth(-1)}
              className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
            >
              ‹
            </button>
            <span className="text-sm font-medium text-gray-800">
              {spec.monthNames[visibleMonth.month - 1]} {visibleMonth.year}
            </span>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => shiftMonth(1)}
              className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] uppercase text-gray-400">
            {spec.weekdayNamesShort.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {grid.map((iso, index) =>
              iso === null ? (
                <span key={`pad-${index}`} />
              ) : (
                <button
                  key={iso}
                  type="button"
                  disabled={outOfRange(iso, min, max)}
                  onClick={() => selectDay(iso)}
                  aria-current={iso === value ? 'date' : undefined}
                  className={`rounded py-1 text-sm disabled:cursor-not-allowed disabled:text-gray-300 ${
                    iso === value
                      ? 'bg-primary-600 text-white'
                      : iso === todayIso()
                        ? 'bg-gray-100 font-medium text-gray-900 hover:bg-gray-200'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {Number(iso.slice(8, 10))}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
